/* RESPONSIBLE TEAM: team-frontend-tech */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable ember/no-classic-classes */
import { resolve } from 'rsvp';
import { isPresent, typeOf, isBlank } from '@ember/utils';
import { A } from '@ember/array';
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import DataCacheItem from '../lib/data-cache-item';
import { STATUS_CODES, ERROR_CODES, generateCacheKey } from '../lib/cache-helpers';

const DEFAULT_NAMESPACE = `EMBER_MODEL_DATA_CACHE`;
const DEFAULT_EXPIRY_TIME = 7;
const DEFAULT_EXPIRY_PERIOD = `days`;
const VALID_EXPIRY_PERIODS = ['hours', 'days', 'weeks'];

let DEFAULT_CACHE_CONFIGURATION = {
  namespace: DEFAULT_NAMESPACE,
  expiryTime: DEFAULT_EXPIRY_TIME,
  expiryPeriod: DEFAULT_EXPIRY_PERIOD,
};

export { generateCacheKey };

export default Service.extend({
  modelConfigurations: null,
  cacheConfiguration: DEFAULT_CACHE_CONFIGURATION,
  scheduledRefreshTasks: {},
  cacheMetrics: A(),
  tracing: service(),

  init() {
    this._super(...arguments);
    this.set('modelConfigurations', {});
  },

  register(modelName, modelConfiguration) {
    let config = Object.assign({}, modelConfiguration, { modelName });
    this._validateModelConfiguration(config);
    this.get(`modelConfigurations`)[modelName] = config;
  },

  fetch(modelName, id) {
    let cache = this;
    let modelConfig = this._cacheConfigurationForModel(modelName);
    let dataCacheItem = DataCacheItem.create({ modelConfig, cache, modelName, id });
    if (modelConfig.disabled) {
      this.log(`⚠️ Caching for ${modelName} disabled. Skipping cache`);
      return modelConfig.fetch(id).then((data) => modelConfig.load(data));
    } else {
      return dataCacheItem
        .get(`fetch`)
        .perform()
        .then((result) => {
          if (isPresent(result.errors)) {
            this._handleFetchError(modelName, result);
          } else {
            this._recordCacheMetric({ model: modelName, status: result.status });
          }
          return result.data;
        });
    }
  },

  hasCachedItem(modelName, id) {
    if (Ember.testing && !window.__testing_ember_model_data_cache__) {
      return false;
    } else if (!this._hasCacheConfigurationForModel(modelName)) {
      return false;
    } else {
      let cache = this;
      let modelConfig = this._cacheConfigurationForModel(modelName);
      let dataCacheItem = DataCacheItem.create({ modelConfig, cache, modelName, id });
      return dataCacheItem.hasData();
    }
  },

  updateCachedItem(modelName, id, data) {
    let cache = this;
    let modelConfig = this._cacheConfigurationForModel(modelName);
    let dataCacheItem = DataCacheItem.create({ modelConfig, cache, modelName, id });
    if (dataCacheItem.hasData()) {
      this.log(`👩‍💻 Manually updating cached data for ${modelName}:${id}`);
      dataCacheItem.setData(data);
    } else {
      throw new Error(
        `Attempted to update data for ${modelName}:${id} but this item does not exist in the cache`,
      );
    }
  },

  clear() {
    this.log(`🚫 Clearing cache`);
    DataCacheItem.clearAll(this.get('cacheConfiguration.namespace'));
  },

  configure(options) {
    this._validateCacheConfiguration(options);
    if (isPresent(options.namespace)) {
      this.set(`cacheConfiguration.namespace`, options.namespace);
    }
    if (isPresent(options.expiryTime)) {
      this.set(`cacheConfiguration.expiryTime`, options.expiryTime);
    }
    if (isPresent(options.expiryPeriod)) {
      this.set(`cacheConfiguration.expiryPeriod`, options.expiryPeriod);
    }
    if (isPresent(options.loggingEnabled)) {
      this.set(`cacheConfiguration.loggingEnabled`, options.loggingEnabled);
    }
  },

  awaitRefreshedModel(modelName, id) {
    let key = generateCacheKey(this.get('cacheConfiguration.namespace'), modelName, id);
    let existingTask = this._refreshTaskForKey(key);
    if (existingTask) {
      this.cancelRefresh(modelName, id);
      let cache = this;
      let modelConfig = this._cacheConfigurationForModel(modelName);
      let dataCacheItem = DataCacheItem.create({ modelConfig, cache, modelName, id });
      this.log(`⏭ Advancing refresh task for ${key} to fetch stage`);
      return dataCacheItem.get('fetchFromRemote').perform();
    } else {
      this.log(`⚠️ No refresh task was found for ${key}. Nothing to advance.`);
      return resolve();
    }
  },

  _validateCacheConfiguration(cacheConfiguration) {
    if (
      cacheConfiguration.hasOwnProperty('namespace') &&
      typeOf(cacheConfiguration.namespace) !== 'string'
    ) {
      throw new Error(`Cannot set a non string namespace for the model data cache`);
    }
    if (
      cacheConfiguration.hasOwnProperty('expiryTime') &&
      typeOf(cacheConfiguration.expiryTime) !== 'number'
    ) {
      throw new Error(`Cannot set a non numeric expiry time for the model data cache`);
    }
    if (
      cacheConfiguration.hasOwnProperty('expiryPeriod') &&
      !VALID_EXPIRY_PERIODS.includes(cacheConfiguration.expiryPeriod)
    ) {
      throw new Error(
        `The model data cache expiry period must be one of 'hours', 'days' or 'weeks'`,
      );
    }
  },

  _validateModelConfiguration(modelConfiguration) {
    if (isBlank(modelConfiguration.modelName)) {
      throw new Error(
        `You must provide a model name when registering a model in the model data cache service`,
      );
    }
    if (typeOf(modelConfiguration.fetch) !== `function`) {
      throw new Error(
        `Error registering ${modelConfiguration.modelName} as a cacheable model: No 'fetch' function was provided`,
      );
    }
    if (typeOf(modelConfiguration.load) !== `function`) {
      throw new Error(
        `Error registering ${modelConfiguration.modelName} as a cacheable model: No 'load' function was provided`,
      );
    }
    if (typeOf(modelConfiguration.refreshDelay) !== `number`) {
      throw new Error(
        `Error registering ${modelConfiguration.modelName} as a cacheable model: A non-numeric refresh delay was provided`,
      );
    }
    if (typeOf(modelConfiguration.fingerprint) !== `string`) {
      throw new Error(
        `Error registering ${modelConfiguration.modelName} as a cacheable model: A non-string fingerprint was provided`,
      );
    }
  },

  _hasCacheConfigurationForModel(modelName) {
    let modelConfigurations = this.get(`modelConfigurations`);
    return modelConfigurations.hasOwnProperty(modelName);
  },

  _cacheConfigurationForModel(modelName) {
    let modelConfigurations = this.get(`modelConfigurations`);
    if (!this._hasCacheConfigurationForModel(modelName)) {
      throw new Error(
        `Attempted to fetch data for the '${modelName}' model through the data cache service, but this model was never registered as a cacheable model.`,
      );
    }
    return modelConfigurations[modelName];
  },

  _handleFetchError(modelName, result) {
    result.errors.forEach((error) => {
      let errorName = Object.keys(ERROR_CODES).find((key) => ERROR_CODES[key] === error.code);
      this.cacheMetrics.pushObject({ model: modelName, status: errorName });
    });
    if (result.errors.some((error) => error.code === ERROR_CODES.QUOTA_EXCEEDED)) {
      console.error(`The localStorage data quota was exceeded. Clearing cache.`);
      this.clear();
      this.tracing.tagRootSpan({ local_storage_quota_exceed: true });
    }
  },

  _recordCacheMetric(metric) {
    let statusName = Object.keys(STATUS_CODES).find((key) => STATUS_CODES[key] === metric.status);
    this.log(`📈 Recording fetch metric : '${metric.model}' ${statusName}`);
    this.cacheMetrics.pushObject({ model: metric.model, status: statusName });
  },

  cancelRefresh(modelName, id) {
    let key = generateCacheKey(this.get('cacheConfiguration.namespace'), modelName, id);
    if (Em.isPresent(this._refreshTaskForKey(key))) {
      this._cancelExistingRefreshTaskForKey(key);
      this._removeRefreshTask(key);
    } else {
      this.log(`No existing refresh task to cancel for ${key}`);
    }
  },

  _addRefreshTask(key, task) {
    this._cancelExistingRefreshTaskForKey(key);
    let scheduledRefreshTasks = this.scheduledRefreshTasks;
    scheduledRefreshTasks[key] = task;
  },

  _removeRefreshTask(key) {
    let scheduledRefreshTasks = this.scheduledRefreshTasks;
    delete scheduledRefreshTasks[key];
  },

  _refreshTaskForKey(key) {
    let scheduledRefreshTasks = this.scheduledRefreshTasks;
    return scheduledRefreshTasks[key];
  },

  _cancelExistingRefreshTaskForKey(key) {
    let existingTask = this._refreshTaskForKey(key);
    if (Em.isPresent(existingTask)) {
      this.log(`Canceling existing refresh task for ${key}`);
      existingTask.cancel();
    }
  },

  log(line) {
    if (this.get('cacheConfiguration.loggingEnabled')) {
      console.debug(`MODEL DATA CACHE : ${line}`);
    }
  },
});
